import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography } from '@mui/material';
import {
  RiFileTextLine,
  RiScan2Line,
  RiFileListLine,
  RiCheckLine,
  RiExternalLinkLine
} from '@remixicon/react';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import styles from './styles';

type NodeData = {
  label: string;
  nodeType: string;
  count?: string;
  onDeleteNode?: (_id: string) => void;
  onSettingsClick?: (_id: string, _nodeType: string, _label: string) => void;
};

const getNodeIcon = (nodeType: string) => {
  switch (nodeType) {
    case 'document_input':
      return <RiFileTextLine size={24} color="#0066FF" />;
    case 'digitize':
      return <RiScan2Line size={24} color="#0066FF" />;
    case 'document_type':
      return <RiFileListLine size={24} color="#0066FF" />;
    case 'document_review':
      return <RiCheckLine size={24} color="#0066FF" />;
    case 'document_export':
      return <RiExternalLinkLine size={24} color="#0066FF" />;
    default:
      return <RiFileTextLine size={24} color="#0066FF" />;
  }
};

const isStartNode = (nodeType: string) => nodeType === 'document_input';
const isEndNode = (nodeType: string) => nodeType === 'document_export';

const CustomNode: React.FC<NodeProps<NodeData>> = ({ id, data, selected }) => {
  const onDeleteNode = data.onDeleteNode;
  const [hovered, setHovered] = React.useState(false);

  const getNodeStyle = (nodeType: string) => {
    const baseStyle = { ...styles.baseStyles, border: selected ? '2px solid #0066FF' : '1px solid #E5E7EB' };

    if (isStartNode(nodeType)) {
      return {
        ...baseStyle,
        borderRadius: '40px 8px 8px 40px',
      };
    }

    if (isEndNode(nodeType)) {
      return {
        ...baseStyle,
        borderRadius: '8px 40px 40px 8px',
      };
    }

    return baseStyle;
  };

  const showActions = selected || hovered;

  return (
    <Box
      sx={styles.containerStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showActions && (
        <Box sx={styles.actionsContainer}>
          <Box
            sx={styles.actionIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (data.onSettingsClick) data.onSettingsClick(id, data.nodeType, data.label);
            }}
          >
            <SettingsIcon sx={{ color: '#6F7287', fontSize: 24 }} />
          </Box>
          <Box
            sx={styles.actionIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (onDeleteNode) onDeleteNode(id);
            }}
          >
            <DeleteIcon sx={{ color: '#6F7287', fontSize: 24 }} />
          </Box>
        </Box>
      )}
      <Box sx={getNodeStyle(data.nodeType)}>
        {!isStartNode(data.nodeType) && (
          <Handle
            type="target"
            position={Position.Left}
            style={styles.handle}
            id="target-left"
          />
        )}

        {getNodeIcon(data.nodeType)}

        {!isEndNode(data.nodeType) && (
          <Handle
            type="source"
            position={Position.Right}
            style={styles.handle}
            id="source-right"
          />
        )}
      </Box>

      <Typography
        sx={styles.nodeLabel}
      >
        {data.label}
      </Typography>

      <Box sx={styles.documentsConteiner}>
        <RiFileListLine size={16} color="#6F7287" style={styles.documentIcon} />
        <Typography sx={styles.documentLabel}>
          {data.count || '0/0'}
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomNode;
